import { GridColDef, isAutogeneratedRow } from "@mui/x-data-grid-premium";
import { DocumentListItemDTO } from "../../types/DocumentListItemDTO";
import DocumentStatusChip from "./DocumentStatusChip";
import { formatCurrencyAmount } from "../../utils/formatters";
import { useAppTranslation } from "../../hooks/useAppTranslation";
import { formatDateToDDMMYYYY } from "../../utils/dateFormetter";
import AssignedUserChip from "./AssignedUserChip";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { IconButton, Tooltip } from "@mui/material";
import PaymentStatusChip from "../payments/PaymentStatusChip";
import {
  getDocumentStatusOptions,
  getPaymentStatusOptions,
} from "../../constants/statusFilterOptions";
import {
  isDocumentServerSideFilterable,
  isDocumentServerSideSortable,
} from "../../constants/documentServerSideFields";

const DocumentTypeCell = ({ value }: { value?: string | null }) => {
  const { t } = useAppTranslation("documents");

  if (!value) {
    return null;
  }

  return <>{t(value, {}, "documents")}</>;
};

export type OwnerOption = { value: string; label: string };
export type PaymentMethodOption = { value: string; label: string };

export type DocumentColumnPermissions = {
  canMarkProcessedExternally: boolean;
  canMarkProcessed: boolean;
};

export const createDocumentColumns = (
  t: (key: string, options?: any, ns?: any) => string,
  onToggleProcessedExternally: (
    row: DocumentListItemDTO,
    value: boolean
  ) => void,
  onToggleProcessed: (
    row: DocumentListItemDTO,
    value: boolean
  ) => void,
  ownerOptions?: OwnerOption[],
  paymentMethodOptions?: PaymentMethodOption[],
  permissions?: DocumentColumnPermissions
): GridColDef<DocumentListItemDTO>[] => {
  const canMarkProcessedExternally =
    permissions?.canMarkProcessedExternally ?? false;

  const canMarkProcessed =
    permissions?.canMarkProcessed ?? false;

  const canSort = (field: string) => isDocumentServerSideSortable(field);
  const canFilter = (field: string) => isDocumentServerSideFilterable(field);

  const ownerLabelByEmail = new Map(
    (ownerOptions ?? []).map((option) => [option.value, option.label])
  );

  const getOwnerLabel = (value?: unknown) => {
    const email = String(value ?? "").trim();

    if (!email) {
      return t("Brak właściciela");
    }

    return ownerLabelByEmail.get(email) ?? email;
  };

  const paymentMethodLabelByCode = new Map(
    (paymentMethodOptions ?? []).map((option) => [option.value, option.label])
  );

  const getPaymentMethodLabel = (value?: unknown) => {
    const code = String(value ?? "").trim();

    if (!code) {
      return "";
    }

    return paymentMethodLabelByCode.get(code) ?? code;
  };

  const columns: GridColDef<DocumentListItemDTO>[] = [
  {
    field: "entryNo",
    headerName: t("entryNo"),
    type: "number",
    sortable: canSort("entryNo"),
    filterable: canFilter("entryNo"),
    aggregable: false,
    flex: 0.5,
  },
  {
    field: "documentDate",
    headerName: t("documentDate"),
    type: "date",
    sortable: canSort("documentDate"),
    filterable: canFilter("documentDate"),
    aggregable: false,
    flex: 1,
    valueGetter: (value) => (value ? new Date(value) : null),
    valueFormatter: (value) => formatDateToDDMMYYYY(value),
  },
  {
    field: "documentNo",
    headerName: t("documentNo"),
    sortable: canSort("documentNo"),
    filterable: canFilter("documentNo"),
    aggregable: false,
    flex: 1,
  },
  {
    field: "vendorName",
    headerName: t("vendorName"),
    sortable: canSort("vendorName"),
    filterable: canFilter("vendorName"),
    aggregable: false,
    flex: 1,
  },
  {
    field: "vendorVatReg",
    headerName: t("vendorVatReg"),
    sortable: canSort("vendorVatReg"),
    filterable: canFilter("vendorVatReg"),
    aggregable: false,
    flex: 1,
  },
  {
    field: "amountGross",
    headerName: t("amountGross"),
    type: "number",
    sortable: canSort("amountGross"),
    filterable: canFilter("amountGross"),
    aggregable: true,
    availableAggregationFunctions: ["sum", "avg", "min", "max"],
    valueGetter: (_value, row) =>
      isAutogeneratedRow(row) ? undefined : row.amountGross,
    valueFormatter: (value) =>
      value === undefined || value === null
        ? ""
        : formatCurrencyAmount(value),
  },
  {
    field: "currency",
    headerName: t("currency"),
    sortable: canSort("currency"),
    filterable: canFilter("currency"),
    aggregable: false,
    flex: 0.5,
  },
  {
    field: "dueDate",
    headerName: t("dueDate"),
    type: "date",
    sortable: canSort("dueDate"),
    filterable: canFilter("dueDate"),
    aggregable: false,
    flex: 1,
    valueGetter: (value) => (value ? new Date(value) : null),
    valueFormatter: (value) => formatDateToDDMMYYYY(value),
  },
  {
    field: "paymentMethod",
    headerName: t("paymentMethod"),
    type: "singleSelect",
    sortable: canSort("paymentMethod"),
    filterable: canFilter("paymentMethod"),
    aggregable: false,
    flex: 1,
    valueOptions: paymentMethodOptions ?? [],
    valueGetter: (_value, row) =>
      isAutogeneratedRow(row) ? undefined : row.paymentMethod,
    valueFormatter: (value) => getPaymentMethodLabel(value),
    renderCell: (params) => {
      if (params.rowNode?.type === "group") {
        return getPaymentMethodLabel(params.value);
      }

      if (isAutogeneratedRow(params.row)) {
        return null;
      }

      return params.row.paymentMethodDescription || getPaymentMethodLabel(params.value);
    },
  },
  {
    field: "paymentStatus",
    headerName: t("paymentStatus"),
    type: "singleSelect",
    flex: 1,
    sortable: canSort("paymentStatus"),
    filterable: canFilter("paymentStatus"),
    aggregable: false,
    valueOptions: getPaymentStatusOptions(t),
    valueGetter: (_value, row) =>
      isAutogeneratedRow(row)
        ? undefined
        : row.paymentStatus || "__EMPTY__",
    valueFormatter: (value) => {
      if (!value) {
        return "";
      }

      return value === "__EMPTY__"
        ? t("Do zapłaty")
        : t(value, {}, "common");
    },
    renderCell: (params) =>
      isAutogeneratedRow(params.row) ? null : (
        <PaymentStatusChip status={params.value} />
      ),
  },
  {
    field: "ksefNo",
    headerName: t("ksefNo"),
    sortable: canSort("ksefNo"),
    filterable: canFilter("ksefNo"),
    aggregable: false,
    flex: 1,
  },
  {
    field: "purchaseCost",
    headerName: t("purchaseCost"),
    sortable: canSort("purchaseCost"),
    filterable: canFilter("purchaseCost"),
    aggregable: false,
    flex: 1,
    valueGetter: (_value, row) =>
      isAutogeneratedRow(row) ? undefined : row.purchaseCost,
    valueFormatter: (value) => (value ? t(value, {}, "documents") : ""),
    renderCell: (params) => {
      if (params.rowNode?.type === "group") {
        return params.value
          ? t(String(params.value), {}, "documents")
          : t("Brak wartości");
      }

      if (isAutogeneratedRow(params.row)) {
        return null;
      }

      return <DocumentTypeCell value={params.value} />;
    },
  },
  {
    field: "status",
    headerName: t("status"),
    type: "singleSelect",
    flex: 1,
    sortable: canSort("status"),
    filterable: canFilter("status"),
    aggregable: false,
    valueOptions: getDocumentStatusOptions(t),
    valueGetter: (_value, row) =>
      isAutogeneratedRow(row) ? undefined : row.status,
    valueFormatter: (value) => (value ? t(value, {}, "statuses") : ""),
    renderCell: (params) =>
      isAutogeneratedRow(params.row) ? null : (
        <DocumentStatusChip status={params.row.status} />
      ),
  },
  {
    field: "assignedUserEmail",
    headerName: t("assignedUserEmail"),
    type: "singleSelect",
    sortable: canSort("assignedUserEmail"),
    filterable: canFilter("assignedUserEmail"),
    aggregable: false,
    flex: 0.9,
    minWidth: 160,
    valueOptions: ownerOptions ?? [],
    valueFormatter: (value) => getOwnerLabel(value),
    renderCell: (params) => {
      if (params.rowNode?.type === "group") {
        return getOwnerLabel(params.value);
      }

      if (isAutogeneratedRow(params.row)) {
        return null;
      }

      return (
        <AssignedUserChip
          email={params.row.assignedUserEmail}
          displayName={params.row.assignedUserDisplayName}
        />
      );
    },
  },
  {
    field: "processedExternally",
    headerName: t("processedExternally"),
    type: "boolean",
    sortable: canSort("processedExternally"),
    filterable: canFilter("processedExternally"),
    aggregable: false,
    flex: 0.6,
    align: "center",
    headerAlign: "center",
    valueFormatter: (value) => (value ? t("Tak") : t("Nie")),
    renderCell: (params) => {
      if (isAutogeneratedRow(params.row)) {
        return null;
      }

      const value = !!params.value;

      return (
        <Tooltip
          title={
            t("Kliknij, aby oznaczyć dokument jako ") +
            (value
              ? t("nieprzetwarzony zewnętrznie")
              : t("przetworzony zewnętrznie"))
          }
        >
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onToggleProcessedExternally(params.row, !value);
            }}
          >
            {value ? (
              <CheckCircleIcon fontSize="small" color="success" />
            ) : (
              <CancelIcon
                fontSize="small"
                sx={{ color: "text.disabled" }}
              />
            )}
          </IconButton>
        </Tooltip>
      );
    },
  },
  {
    field: "processed",
    headerName: t("Przetworzony"),
    type: "boolean",
    sortable: canSort("processed"),
    filterable: canFilter("processed"),
    aggregable: false,
    flex: 0.6,
    align: "center",
    headerAlign: "center",
    valueFormatter: (value) => (value ? t("Tak") : t("Nie")),
    renderCell: (params) => {
      if (isAutogeneratedRow(params.row)) {
        return null;
      }

      const value = !!params.value;

      return (
        <Tooltip
          title={
            t("Kliknij, aby oznaczyć dokument jako ") +
            (value ? t("nieprzetworzony") : t("przetworzony"))
          }
        >
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onToggleProcessed(params.row, !value);
            }}
          >
            {value ? (
              <CheckCircleIcon fontSize="small" color="success" />
            ) : (
              <CancelIcon
                fontSize="small"
                sx={{ color: "text.disabled" }}
              />
            )}
          </IconButton>
        </Tooltip>
      );
    },
  },
  ];

  return columns.filter((column) => {
    if (column.field === "processedExternally") {
      return canMarkProcessedExternally;
    }

    if (column.field === "processed") {
      return canMarkProcessed;
    }

    return true;
  });
};